import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatINR } from "@/components/ChefCard";
import { ArrowRight, Utensils, ShieldCheck } from "lucide-react";
import DietaryBlock from "@/components/DietaryBlock";
import { fmtLocalDate } from "@/lib/payments";
import TermsConsent from "@/components/TermsConsent";

const CITIES = ["Mumbai", "Bengaluru", "Indore", "Chennai", "Goa", "Dubai"];

export default function MenuDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { requireAuth } = useAuth();
  const [menu, setMenu] = useState(null);
  const [guests, setGuests] = useState(8);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [city, setCity] = useState("Mumbai");
  const [eventDate, setEventDate] = useState(null);
  const [customRequest, setCustomRequest] = useState("");
  const [dietary, setDietary] = useState({
    dietary_preferences: [], allergies: [], chef_notes: "", kitchen_equipment: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/menus/${id}`);
      setMenu(r.data.menu);
      setGuests(r.data.menu.min_guests || 8);
    })();
  }, [id]);

  if (!menu) return <div className="max-w-7xl mx-auto px-6 py-24 text-neutral-500">Loading…</div>;

  const approx = (menu.price_per_head || 0) * guests;
  const commission = 0;
  const gst = Math.round(approx * 0.18);
  const total = approx + gst;

  const submit = async () => {
    if (!requireAuth()) return;
    if (!eventDate) return toast.error("Pick an event date");
    setSubmitting(true);
    try {
      await api.post("/bookings/catering", {
        menu_id: menu.id,
        city,
        event_date: fmtLocalDate(eventDate),
        guests,
        contact_name: contactName,
        contact_phone: contactPhone,
        custom_request: customRequest,
        dietary_preferences: dietary.dietary_preferences,
        allergies: dietary.allergies,
        chef_notes: dietary.chef_notes,
        kitchen_equipment: dietary.kitchen_equipment,
      });
      toast.success("Menu request submitted — our team will confirm within 2 hours.");
      nav("/my-bookings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="grid md:grid-cols-5 gap-10">
        <div className="md:col-span-3">
          <div className="aspect-[4/3] overflow-hidden bg-neutral-900 border border-white/10">
            <img src={menu.image_url} alt={menu.title} className="w-full h-full object-cover" />
          </div>
          <div className="flex items-center gap-3 mt-6">
            <span className="chip text-[#F5B041]">{menu.category}</span>
            {menu.featured && <span className="chip text-[#C04000] border-[#C04000]/40">Signature</span>}
            {menu.tags?.map(t => <span key={t} className="chip">{t}</span>)}
          </div>
          <h1 className="font-serif-display text-5xl mt-6 leading-tight">{menu.title}</h1>
          <p className="font-script text-2xl text-[#F5B041] mt-2">{menu.tagline}</p>
          <div className="dotted-sep my-8" />
          <p className="text-neutral-300 leading-relaxed text-lg">{menu.description}</p>
          <p className="text-neutral-500 text-sm mt-4">{menu.chef_credit}</p>

          <div className="mt-10">
            <div className="small-caps text-neutral-500 mb-4">The Menu</div>
            <ol className="space-y-4">
              {menu.courses?.map((c, i) => (
                <li key={c} className="flex items-start gap-6 border-b border-white/5 pb-4">
                  <div className="font-serif-display text-3xl text-[#C04000] w-10">{String(i + 1).padStart(2, "0")}</div>
                  <div className="font-serif-menu text-xl pt-1">{c}</div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-14">
            <div className="small-caps text-neutral-500 mb-2">Tailor for your household</div>
            <h3 className="font-serif-display text-3xl mb-2">Dietary & lifestyle</h3>
            <p className="text-neutral-400 text-sm mb-8 max-w-lg">
              The more precise you are, the more precise the chef can be. Everything below is optional but recommended.
            </p>
            <DietaryBlock value={dietary} onChange={setDietary} testIdPrefix="menu" />
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="sticky top-24 space-y-6">
            <div className="bg-[#141414] border border-white/10 p-7">
              <div className="small-caps text-[10px] text-neutral-500">Per head</div>
              <div className="font-serif-display text-5xl mt-1">₹{formatINR(menu.price_per_head)}</div>
              <p className="text-neutral-400 text-sm mt-2">Minimum {menu.min_guests} guests. Includes serving staff, plating and cleanup.</p>

              <div className="dotted-sep my-6" />

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Your name</Label>
                    <Input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="event-contact-name"
                      placeholder="Full name"
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11 focus-visible:border-[#C04000] focus-visible:ring-0" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Contact number</Label>
                    <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="event-contact-phone"
                      placeholder="+91 98765 43210"
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11 focus-visible:border-[#C04000] focus-visible:ring-0" />
                  </div>
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Guests</Label>
                  <Input type="number" min={menu.min_guests} value={guests} onChange={(e) => setGuests(parseInt(e.target.value) || 0)}
                    data-testid="guests-input"
                    className="mt-2 rounded-none bg-transparent border-white/10 h-11 focus-visible:border-[#C04000] focus-visible:ring-0" />
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">City</Label>
                  <Select value={city} onValueChange={setCity}>
                    <SelectTrigger data-testid="event-city" className="mt-2 rounded-none bg-transparent border-white/10 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#141414] border-white/10">
                      {CITIES.map(c => <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Event date</Label>
                  <div className="mt-2 border border-white/10 p-3 bg-[#0F0F0F]">
                    <Calendar mode="single" selected={eventDate} onSelect={setEventDate}
                      disabled={{ before: new Date() }} data-testid="event-date-picker" />
                  </div>
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Describe your evening</Label>
                  <Textarea value={customRequest} onChange={(e) => setCustomRequest(e.target.value)}
                    data-testid="custom-request"
                    placeholder="Occasion, dietary tone, wine pairings, allergies, timing…"
                    className="mt-2 rounded-none bg-transparent border-white/10 min-h-28" />
                </div>
              </div>
            </div>

            {/* Approx estimate */}
            <div className="bg-[#1A1A1A] border border-white/10 p-6">
              <div className="small-caps text-neutral-500 text-[10px]">Approximate estimate</div>
              <div className="dotted-sep my-4" />
              <div className="text-sm space-y-2">
                <div className="flex justify-between"><span className="text-neutral-400">{guests} × ₹{formatINR(menu.price_per_head)}</span><span>₹{formatINR(approx)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">GST (18%)</span><span>₹{formatINR(gst)}</span></div>
              </div>
              <div className="dotted-sep my-4" />
              <div className="flex items-end justify-between">
                <span className="small-caps text-[10px] text-neutral-500">Estimated total</span>
                <span className="font-serif-display text-3xl text-[#F5B041]">₹{formatINR(total)}</span>
              </div>
              <p className="text-[10px] text-neutral-500 mt-3">Final quote confirmed by our team based on your brief.</p>
            </div>

            <TermsConsent checked={agreed} onChange={setAgreed} testId="menu-terms-consent" variant="catering" />
            <Button onClick={() => agreed ? submit() : toast.error("Please accept the Terms & Conditions")} disabled={submitting || !agreed} data-testid="request-menu-btn"
              className="w-full rounded-none bg-[#C04000] hover:bg-[#A03500] h-12 small-caps text-[11px]">
              Request this menu <ArrowRight size={14} className="ml-2" />
            </Button>
            <div className="flex items-center gap-2 text-[10px] text-neutral-500">
              <ShieldCheck size={12} className="text-[#F5B041]" /> Confidential brief · Discreet service
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
